import { Component, Inject, OnInit, DestroyRef, HostListener, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AccountService, SubscriptionType } from 'src/app/services/account/account.service';
import { ConsoleLoggerService } from 'src/app/services/logging/console-logger.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { SubscriptionDeleteDialogComponent } from './subscription-delete-dialog/subscription-delete-dialog.component';
import { WebviewService } from 'src/app/services/platform/webview.service';
import { SubscriptionPlatform, UserSubscription } from 'src/app/models/user-subscription';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { environment } from 'src/environments/environment';
import device from 'current-device';
import { InfoDialogComponent } from '../info-dialog/info-dialog.component';
import { PriceType } from 'src/app/models/price-type';
import { HttpService } from 'src/app/services/core/http.service';
import { LocationService } from 'src/app/services/platform/location.service';
import { SubscriptionFeature, SubscriptionPlan } from 'src/app/models/subscription';
import { BillingAddress, Business, Product } from 'src/app/models/billing';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormField, MatOption, MatSelect } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { EvLoaderComponent } from '../../ui-components/ev-loader/ev-loader.component';
import { ModalOptionPanelComponent } from '../modal-option-panel/modal-option-panel.component';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { AccountDialogComponent } from '../account-dialog/account-dialog.component';

const MONTHLY_COST_MONTHLY_PLAN_PRO = 4.99;
const MONTHLY_COST_MONTHLY_PLAN_PROPLUS = 7.99;
/** Bump whenever a screenshot is replaced under the same file name: assets are not content-hashed by the build
 *  and nginx sends no Cache-Control for them, so without a new URL browsers keep showing the old image. */
const SCREENSHOT_VERSION = 2;

/**
   * adding, managing user subscriptions
   */
@Component({
  standalone: true,
  selector: 'app-subscriptions-dialog',
  templateUrl: './subscriptions-dialog.component.html',
  imports: [MatCheckbox, MatOption, MatSelect, MatFormField, CommonModule, ReactiveFormsModule, FormsModule, EvLoaderComponent, ModalOptionPanelComponent],
  styleUrls: ['./subscriptions-dialog.component.scss']
})
export class SubscriptionsDialogComponent implements OnInit {

  /**
   * Every value the template renders is a signal: HTTP responses are delivered outside the
   * Angular zone, so a signal write is what notifies change detection that they changed.
   * Collections are replaced, never mutated in place — mutating an object held by a signal
   * would not notify anything.
   */
  public readonly userSubscription = signal<UserSubscription | undefined>(undefined);
  public readonly voucherInput = signal<string>('');
  public readonly loader = signal<boolean>(false);
  /** Screenshot viewer: which feature's screenshots are open and which one is shown. */
  public readonly openedImage = signal<{ feature: SubscriptionFeature, index: number } | null>(null);
  public readonly viewerImages = computed(() => {
    const opened = this.openedImage();
    return opened ? [opened.feature.screenshot1, opened.feature.screenshot2] : [];
  });
  /** Which way the last step went, so the incoming/outgoing image slide in the matching direction. */
  public readonly viewerSlideDirection = signal<'next' | 'prev'>('next');
  private viewerSwipeStartX: number | null = null;
  public readonly priceType = signal<PriceType>(PriceType.Euro);
  public readonly activeStepIndex = signal<number>(0);

  public readonly subscriptionFeatures = signal<SubscriptionFeature[]>([]);
  public readonly subscriptionPlans = signal<SubscriptionPlan[]>([]);
  public readonly activePlan = signal<SubscriptionPlan | null>(null);
  public readonly countryList = signal<{ name: string, dial_code: string, code: string }[]>([]);

  /** Which feature row is expanded — used to be a `dropdown` flag mutated on each feature object. */
  public readonly expandedFeature = signal<number | null>(null);

  public PriceType = PriceType;

  public packageList = ["free", "pro", "proplus"];
  private readonly subscriptionOrder = [
    SubscriptionType.Free,
    SubscriptionType.ProMonthly,
    SubscriptionType.ProYearly,
    SubscriptionType.ProplusMonthly,
    SubscriptionType.ProplusYearly,
    SubscriptionType.ProplusVoucher
  ];

  private readonly destroyRef = inject(DestroyRef);
  public subscriptionFormGroup!: FormGroup;

  constructor(public dialogRef: MatDialogRef<SubscriptionsDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: any,
    public languageService: LanguageService, private readonly fb: FormBuilder,
    public settingsService: SettingsService, public accountService: AccountService, private readonly consoleLoggerService: ConsoleLoggerService,
    private readonly webviewService: WebviewService, private readonly utilsService: UtilsService,
    private readonly httpService: HttpService, private readonly locationService: LocationService,
    private readonly stackedModalsService: StackedModalsService) {
  }

  get name() { return this.subscriptionFormGroup.get('name')!; }
  get country() { return this.subscriptionFormGroup.get('country')!; }
  get city() { return this.subscriptionFormGroup.get('city')!; }
  get addressLine1() { return this.subscriptionFormGroup.get('addressLine1')!; }
  get addressLine2() { return this.subscriptionFormGroup.get('addressLine2')!; }
  get postalCode() { return this.subscriptionFormGroup.get('postalCode')!; }
  get business() { return this.subscriptionFormGroup.get('business')!; }
  get businessName() { return this.subscriptionFormGroup.get('businessName')!; }
  get vatNumber() { return this.subscriptionFormGroup.get('vatNumber')!; }

  ngOnInit(): void {
    const lastPos = this.locationService.getLastPosition();
    if (lastPos != null) {
      this.httpService.getReverseGeocode(lastPos.Latitude, lastPos.Longitude).subscribe(
        {
          next: (resp: any) => {
            if (resp.country) {
              this.priceType.set(this.utilsService.isEURCountry(resp.country) ? PriceType.Euro : PriceType.Dollar);
            }
          }
        });
    }

    this.httpService.getCountryListJSON("en").subscribe((resp: any) => {
      this.countryList.set(resp);
    });

    this.subscriptionFormGroup = this.fb.group({
      name: ['', [
        Validators.required
      ]],
      country: ['', [
        Validators.required
      ]],
      city: ['', [
        Validators.required
      ]],
      addressLine1: ['', [
        Validators.required
      ]],
      addressLine2: [''],
      postalCode: ['', [
        Validators.required
      ]],
      business: [false],
      businessName: [''],
      vatNumber: ['']
    });

    this.country.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.vatNumber.setValue('', { emitEvent: false });
      this.updateVatNumberValidators();
    });

    this.subscriptionFeatures.set([
      {
        shortDescription: this.languageService.languageJSON.SubscriptionsDialog_TurnByTurn_ShortDescription,
        free: true, pro: true, proplus: true,
        longDescription: this.languageService.languageJSON.SubscriptionsDialog_TurnByTurn_LongDescription,
        screenshot1: "screenshot_turn_by_turn.png", screenshot2: "screenshot_turn_by_turn_2.png"
      },
      {
        shortDescription: this.languageService.languageJSON.SubscriptionsDialog_ChargingDatabase_ShortDescription,
        free: true, pro: true, proplus: true,
        longDescription: this.languageService.languageJSON.SubscriptionsDialog_ChargingDatabase_LongDescription,
        screenshot1: "screenshot_charging_database.png", screenshot2: "screenshot_charging_database_2.png"
      },
      {
        shortDescription: this.languageService.languageJSON.SubscriptionsDialog_RoutePlanning500km_ShortDescription,
        free: false, pro: true, proplus: true,
        longDescription: this.languageService.languageJSON.SubscriptionsDialog_RoutePlanning500km_LongDescription,
        screenshot1: "screenshot_route_planning.png", screenshot2: "screenshot_route_planning_2.png"
      },

      {
        shortDescription: this.languageService.languageJSON.SubscriptionsDialog_VoiceInput_ShortDescription,
        free: false, pro: true, proplus: true,
        longDescription: this.languageService.languageJSON.SubscriptionsDialog_VoiceInput_LongDescription,
        screenshot1: "screenshot_voice.png", screenshot2: "screenshot_voice_2.png"
      },
      {
        shortDescription: this.languageService.languageJSON.SubscriptionsDialog_AndroidAutoCarPlay_ShortDescription,
        free: false, pro: true, proplus: true,
        longDescription: this.languageService.languageJSON.SubscriptionsDialog_AndroidAutoCarPlay_LongDescription,
        screenshot1: "screenshot_androidauto.png", screenshot2: "screenshot_carplay.png", landscapeImage: true
      },
      {
        shortDescription: this.languageService.languageJSON.SubscriptionsDialog_WeatherForecast_ShortDescription,
        free: false, pro: true, proplus: true,
        longDescription: this.languageService.languageJSON.SubscriptionsDialog_WeatherForecast_LongDescription,
        screenshot1: "screenshot_weather.png", screenshot2: "screenshot_weather_2.png"
      },
      {
        shortDescription: this.languageService.languageJSON.SubscriptionsDialog_LiveChargingStatus_ShortDescription,
        free: false, pro: false, proplus: true,
        longDescription: this.languageService.languageJSON.SubscriptionsDialog_LiveChargingStatus_LongDescription,
        screenshot1: "screenshot_charging_status.png", screenshot2: "screenshot_charging_status_2.png"
      },
      {
        shortDescription: this.languageService.languageJSON.SubscriptionsDialog_LiveTraffic_ShortDescription,
        free: false, pro: false, proplus: true,
        longDescription: this.languageService.languageJSON.SubscriptionsDialog_LiveTraffic_LongDescription,
        screenshot1: "screenshot_traffic.png", screenshot2: "screenshot_traffic_2.png"
      }
    ]);

    this.preloadScreenshots();

    this.subscriptionPlans.set([
      {
        type: "pro",
        monthlyCostMonthlyPlan: MONTHLY_COST_MONTHLY_PLAN_PRO,
        monthlyCostYearlyPlan: Math.round(MONTHLY_COST_MONTHLY_PLAN_PRO * 10 / 12 * 100) / 100,
        yearlyCostMonthlyPlan: Math.round(MONTHLY_COST_MONTHLY_PLAN_PRO * 12 * 100) / 100,
        yearlyCostYearlyPlan: Math.round(MONTHLY_COST_MONTHLY_PLAN_PRO * 10 * 100) / 100,
        period: "yearly",
      },
      {
        type: "proplus",
        monthlyCostMonthlyPlan: MONTHLY_COST_MONTHLY_PLAN_PROPLUS,
        monthlyCostYearlyPlan: Math.round(MONTHLY_COST_MONTHLY_PLAN_PROPLUS * 10 / 12 * 100) / 100,
        yearlyCostMonthlyPlan: Math.round(MONTHLY_COST_MONTHLY_PLAN_PROPLUS * 12 * 100) / 100,
        yearlyCostYearlyPlan: Math.round(MONTHLY_COST_MONTHLY_PLAN_PROPLUS * 10 * 100) / 100,
        period: "yearly"
      }
    ]);

    this.accountService.ObservableSubscriptionType.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (resp) => {
        if (resp) {
          this.userSubscription.set(this.accountService.getUserSubscription());
          this.loader.set(false);
          this.setAllPlanPeriods("yearly");
          this.checkForOnlyMonthlySubscription();
        }
      }
    });

    this.accountService.ObservableSubscriptionFailed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (resp) => {
        if (resp) {
          this.stackedModalsService.openModalWindow(InfoDialogComponent, {
            panelClass: "subscription-info",
            data: {
              title: this.languageService.languageJSON.Global_Error,
              message: this.languageService.languageJSON.AccountDialog_Verification_Error
            },
            autoFocus: false
          });

          this.accountService.ObservableSubscriptionFailed.next(false);
        }
      }
    });

    this.accountService.ObservableSubscriptionLoader.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (resp) => {
        if (resp) {
          this.loader.set(true);
        }
      }
    });

    if (this.accountService.getIsAuthorized()) {
      this.loader.set(true);
      this.accountService.loadUserProfile().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.userSubscription.set(this.accountService.getUserSubscription());
          this.loader.set(false);
          this.checkForOnlyMonthlySubscription();
        }
      });
    }
  }

  private checkForOnlyMonthlySubscription(): void {
    const subscriptionType = this.userSubscription()?.SubscriptionType;
    if (subscriptionType == SubscriptionType.ProYearly) {
      this.setPlanPeriod("pro", "monthly");
    }
    if (subscriptionType == SubscriptionType.ProplusYearly) {
      this.setPlanPeriod("proplus", "monthly");
    }
  }

  /**
   * Plans are replaced rather than mutated — assigning plan.period in place would leave the
   * signal holding the same array reference and notify nothing.
   */
  public setPlanPeriod(type: string, period: string): void {
    this.subscriptionPlans.update(plans => plans.map(plan => plan.type === type ? { ...plan, period } : plan));
  }

  private setAllPlanPeriods(period: string): void {
    this.subscriptionPlans.update(plans => plans.map(plan => ({ ...plan, period })));
  }

  /** Feature rows used to carry their own mutable `dropdown` flag. */
  /** Fetch every feature screenshot up front so they are already cached by the
   *  time a row is expanded, instead of popping in after the open animation. */
  private preloadScreenshots(): void {
    for (const feature of this.subscriptionFeatures()) {
      for (const screenshot of [feature.screenshot1, feature.screenshot2]) {
        new Image().src = this.screenshotUrl(screenshot);
      }
    }
  }

  public screenshotUrl(screenshot: string): string {
    return 'assets/screenshots/light/' + screenshot + '?v=' + SCREENSHOT_VERSION;
  }

  public toggleFeature(index: number): void {
    this.expandedFeature.update(current => current === index ? null : index);
  }

  public onVatNumberChanged(): void {
    if (this.vatNumber.valid) {
      this.vatNumber.setErrors(null);
    }
  }

  public onVatNumberInput(event: any): void {
    if (this.country.value === 'HU') {
      const formatted = this.formatHungarianVat(event.target.value);
      this.vatNumber.setValue(formatted, { emitEvent: false });
    }
    this.onVatNumberChanged();
  }

  /**
   * Formats a Hungarian VAT number to the XXXXXXXX-Y-ZZ standard while typing.
   */
  private formatHungarianVat(value: string): string {
    const digits = (value || '').replaceAll(/\D/g, '').slice(0, 11);
    let result = digits.slice(0, 8);
    if (digits.length > 8) {
      result += '-' + digits.slice(8, 9);
    }
    if (digits.length > 9) {
      result += '-' + digits.slice(9, 11);
    }
    return result;
  }

  private hungarianVatValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return { required: true };
      }
      return /^\d{8}-\d-\d{2}$/.test(control.value) ? null : { invalidVat: true };
    };
  }

  /**
   * Sets the VAT number validators based on the selected country:
   * - non-EU VAT country (or no business): no validation, anything is allowed
   * - EU VAT country other than Hungary: required, at least 2 characters (validated server-side on subscribe)
   * - Hungary: front-end validated against the XXXXXXXX-Y-ZZ standard
   */
  private updateVatNumberValidators(): void {
    const country = this.country.value;
    if (!this.business.value || !this.utilsService.isEUVatCountry(country)) {
      this.vatNumber.clearValidators();
    } else if (country === 'HU') {
      this.vatNumber.setValidators([this.hungarianVatValidator()]);
    } else {
      this.vatNumber.setValidators([Validators.required, Validators.minLength(2)]);
    }
    this.vatNumber.updateValueAndValidity();
  }

  public getPlatform(): string {
    switch (this.userSubscription()!.Platform) {
      case SubscriptionPlatform.Web:
        return "Web";
      case SubscriptionPlatform.AndroidApp:
        return "Android";
      case SubscriptionPlatform.iOSApp:
        return "iOS";
      default:
        return "Web";
    }
  }

  private getSubscriptionRank(subscriptionType: SubscriptionType): number {
    switch (subscriptionType) {
      case SubscriptionType.Free: return 0;
      case SubscriptionType.ProMonthly: return 1;
      case SubscriptionType.ProYearly: return 2;
      case SubscriptionType.ProplusMonthly: return 3;
      case SubscriptionType.ProplusYearly: return 4;
      case SubscriptionType.ProplusVoucher: return 5;
      default: return 0;
    }
  }

  private getPlanRank(plan: SubscriptionPlan): number {
    if (plan.type === "pro" && plan.period === "monthly") { return 1; }
    if (plan.type === "pro" && plan.period === "yearly") { return 2; }
    if (plan.type === "proplus" && plan.period === "monthly") { return 3; }
    if (plan.type === "proplus" && plan.period === "yearly") { return 4; }
    return 0;
  }

  public getPlanButtonText(plan: SubscriptionPlan): string {
    if (this.userSubscription()?.SubscriptionType === SubscriptionType.Free && !this.accountService.user.TrialUsed) {
      return this.languageService.languageJSON.SubscriptionsDialog_TryNow;
    }
    if (this.userSubscription()?.AutoRenew) {
      const currentRank = this.getSubscriptionRank(this.userSubscription()!.SubscriptionType);
      const planRank = this.getPlanRank(plan);
      if (planRank > currentRank) {
        return this.languageService.languageJSON.SubscriptionsDialog_Upgrade;
      }
      if (planRank < currentRank) {
        return this.languageService.languageJSON.SubscriptionsDialog_Downgrade;
      }
    }

    return this.languageService.languageJSON.Global_Subscribe;
  }

  public getSubscriptionTypeName(subscriptionType: SubscriptionType): string {
    if (subscriptionType.includes("proplus")) {
      return "PROPLUS";
    }
    else if (subscriptionType.includes("pro")) {
      return "PRO";
    }
    else {
      return "FREE";
    }
  }

  public getSubscriptionPeriodName(userSubscription: UserSubscription): string {
    if (!userSubscription.AutoRenew) {
      if (userSubscription.SubscriptionType == SubscriptionType.ProplusVoucher) {
        return this.languageService.languageJSON.SubscriptionsDialog_Voucher;
      }

      return this.languageService.languageJSON.SubscriptionsDialog_Cancelled;
    }
    else if (userSubscription.SubscriptionType.includes("yearly")) {
      return this.languageService.languageJSON.SubscriptionsDialog_YearlyPlan;
    }
    else {
      return this.languageService.languageJSON.SubscriptionsDialog_MonthlyPlan;
    }
  }

  public getSubscriptionPeriodNameBySubscriptionType(subscriptionType: SubscriptionType): string {
    if (subscriptionType.includes("yearly")) {
      return this.languageService.languageJSON.SubscriptionsDialog_YearlyPlan;
    }
    else {
      return this.languageService.languageJSON.SubscriptionsDialog_MonthlyPlan;
    }
  }

  public selectSubscription(plan: SubscriptionPlan): void {
    if (!this.accountService.getIsAuthorized()) {
      this.dialogRef.close();
      this.stackedModalsService.openModalWindow(AccountDialogComponent, {
        data: { process: 'signIn' },
        autoFocus: false,
        panelClass: 'dialog-snap-to-fullscreen'
      });

      return;
    }

    const userSubscription = this.userSubscription();
    // The profile request may still be in flight (the loader is visible) — without a loaded
    // subscription there is nothing to validate the plan change against.
    if (!userSubscription) {
      return;
    }

    if (userSubscription.SubscriptionType != SubscriptionType.Free && !this.isSubscriptionAddedOnThisPlatform()) {
      this.stackedModalsService.openModalWindow(InfoDialogComponent, {
        panelClass: "subscription-info",
        data: { message: this.languageService.languageJSON.SubscriptionsDialog_ChangePlatformErrorMessage.replaceAll("$platform", this.getPlatform()) },
        autoFocus: false
      });

      return;
    }

    const subscriptionType = `evnavigation.${plan.type}.${plan.period}` as SubscriptionType;
    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.StartSubscription(subscriptionType);
    }
    else {
      this.activeStepIndex.set(1);
      this.activePlan.set(plan);
    }
  }

  public subscribeToSubscription() {
    if (!this.subscriptionFormGroup.valid) {
      this.subscriptionFormGroup.markAllAsTouched();
      return;
    }

    const country = this.country.value;
    // Hungarian VAT numbers are validated on the front-end; non-EU VAT countries accept anything.
    // Only EU VAT countries other than Hungary are verified server-side.
    const needsEuVatCheck = this.business.value && this.utilsService.isEUVatCountry(country) && country !== 'HU';
    if (!needsEuVatCheck) {
      this.doSubscribe();
      return;
    }

    this.loader.set(true);
    this.httpService.getVATEUCheck(this.vatNumber.value, this.country.value).subscribe({
      next: (res: any) => {
        if (!res?.isValid) {
          this.loader.set(false);
          this.vatNumber.setErrors({ 'invalidVat': true });
          this.vatNumber.markAsTouched();
          return;
        }
        this.doSubscribe();
      },
      error: () => {
        this.loader.set(false);
        this.vatNumber.setErrors({ 'invalidVat': true });
        this.vatNumber.markAsTouched();
      }
    });
  }

  private doSubscribe() {
    const subscriptionType = `evnavigation.${this.activePlan()!.type}.${this.activePlan()!.period}` as SubscriptionType;

    const billingAddress: BillingAddress = {
      country: this.country.value,
      city: this.city.value,
      postal_code: this.postalCode.value,
      address_line_1: this.addressLine1.value,
      address_line_2: this.addressLine2.value,
      name: this.name.value
    };

    const business: Business | null = this.business.value ? {
      name: this.businessName.value,
      vat: this.vatNumber.value
    } : null;

    const product: Product = {
      name: subscriptionType,
      priceId: this.getPriceId(this.activePlan()!)
    };

    this.loader.set(true);
    this.httpService.subscribeToSubscription(billingAddress, business, product).subscribe({
      next: (res) => {
        this.loader.set(false);
        if (res?.url) {
          this.utilsService.openUrlOnSameTab(this.httpService.getUserSubscriptionUrl(res.url));
        }
      },
      error: (error) => {
        this.loader.set(false);
        if (error.error?.message == "VAT_INVALID") {
          this.vatNumber.setErrors({ 'invalidVat': true });
        }
      }
    });
  }

  public onBusinessChange(event: any): void {
    if (event.checked) {
      this.businessName.setValidators([Validators.required]);
    } else {
      this.businessName.clearValidators();
    }
    this.businessName.updateValueAndValidity();
    this.updateVatNumberValidators();
  }

  private getPriceId(plan: SubscriptionPlan) {
    const planType = this.subscriptionPlans().find((el) => { return el.type == plan.type })!;
    let priceId = `STRIPE_${planType.type.toUpperCase()}_${planType.period.toUpperCase()}_PRICE_ID_${this.priceType() == PriceType.Euro ? "EUR" : "USD"}`;
    return environment[priceId];
  }

  public compareSubscriptions(sub1: UserSubscription, sub2: string): number {
    if (!sub1.AutoRenew && sub1.SubscriptionType != SubscriptionType.ProplusVoucher) {
      return sub1.SubscriptionType == sub2 ? 1 : 0;
    }

    const index1 = this.subscriptionOrder.indexOf(sub1.SubscriptionType);
    const index2 = this.subscriptionOrder.indexOf(sub2 as SubscriptionType);

    return (index1 >= index2) ? 1 : 0;
  }

  public activateVoucher(): void {
    const voucherToken = "EV-" + this.voucherInput();

    this.accountService.addVoucherSubscription(voucherToken).subscribe((resp) => {
      if (resp) {
        this.dialogRef.close();
      }
    });
  }

  public openImageViewer(feature: SubscriptionFeature, index: number): void {
    this.viewerSlideDirection.set('next');
    this.openedImage.set({ feature, index });
  }

  public closeImageViewer(): void {
    this.openedImage.set(null);
  }

  /** Step to the previous/next screenshot of the opened feature, wrapping around at both ends. */
  public stepImageViewer(delta: 1 | -1, event?: Event): void {
    event?.stopPropagation();
    const opened = this.openedImage();
    if (!opened) {
      return;
    }
    const count = this.viewerImages().length;
    this.viewerSlideDirection.set(delta > 0 ? 'next' : 'prev');
    this.openedImage.set({ ...opened, index: (opened.index + delta + count) % count });
  }

  public showImageViewerAt(index: number, event?: Event): void {
    event?.stopPropagation();
    const opened = this.openedImage();
    if (!opened || opened.index === index) {
      return;
    }
    this.viewerSlideDirection.set(index > opened.index ? 'next' : 'prev');
    this.openedImage.set({ ...opened, index });
  }

  @HostListener('document:keydown', ['$event'])
  public onImageViewerKeydown(event: KeyboardEvent): void {
    if (!this.openedImage()) {
      return;
    }
    switch (event.key) {
      case 'ArrowRight': this.stepImageViewer(1); break;
      case 'ArrowLeft': this.stepImageViewer(-1); break;
      case 'Escape': this.closeImageViewer(); break;
      default: return;
    }
    event.preventDefault();
  }

  public onImageViewerPointerDown(event: PointerEvent): void {
    this.viewerSwipeStartX = event.clientX;
  }

  /** A horizontal drag of at least 40px across the image counts as a swipe to the next/previous screenshot. */
  public onImageViewerPointerUp(event: PointerEvent): void {
    const startX = this.viewerSwipeStartX;
    this.viewerSwipeStartX = null;
    if (startX == null) {
      return;
    }
    const dx = event.clientX - startX;
    if (Math.abs(dx) >= 40) {
      this.stepImageViewer(dx < 0 ? 1 : -1);
    }
  }

  public isSubscriptionAddedOnThisPlatform(): boolean {
    const userSubscription = this.userSubscription();
    if (!userSubscription) {
      return false;
    }
    if (!this.webviewService.IsGPSTWebview() && userSubscription.Platform == SubscriptionPlatform.Web) {
      return true;
    }
    if (this.webviewService.IsGPSTWebview() && device.android() && userSubscription.Platform == SubscriptionPlatform.AndroidApp) {
      return true;
    }
    if (this.webviewService.IsGPSTWebview() && device.ios() && userSubscription.Platform == SubscriptionPlatform.iOSApp) {
      return true;
    }
    return false;
  }

  public cancelSubscription() {
    if (!this.isSubscriptionAddedOnThisPlatform()) {
      this.stackedModalsService.openModalWindow(InfoDialogComponent, {
        panelClass: "subscription-info",
        data: { message: this.languageService.languageJSON.SubscriptionsDialog_CancelPlatformErrorMessage.replaceAll("$platform", this.getPlatform()) },
        autoFocus: false
      });

      return;
    }

    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.OpenManageSubscription();
    }
    else {
      const dialog = this.stackedModalsService.openModalWindow(SubscriptionDeleteDialogComponent,
        {
          data: { subscriptionType: this.userSubscription() },
          autoFocus: false
        });

      dialog.afterClosed().subscribe((resp) => {
        // Closed with Escape or the backdrop: no result, nothing to cancel.
        if (resp?.delete) {
          this.loader.set(true);
          this.accountService.cancelUserSubscription().subscribe({
            next: () => {
              this.accountService.fetchWithRetryForSubscription(2, 10000, this.userSubscription()!.SubscriptionType, "false").subscribe({
                next: (resp) => {
                  this.accountService.setUserData(resp);
                  this.accountService.parseUserSubscription(resp);
                  this.loader.set(false);
                },
                error: () => {
                  this.loader.set(false);
                }
              });
            },
            error: () => { this.loader.set(false); }
          });
        }
      });
    }
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public finish(): void {
    this.dialogRef.close();
  }
}
